import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, X, Download, UploadSimple, FileCsv } from "@phosphor-icons/react";

/**
 * Generic CRUD page component.
 * schema: { title, subtitle, path, columns: [{key,label,render?}], fields: [{key,label,type,options?,required}] }
 */
export default function CrudPage({ schema }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [remote, setRemote] = useState({}); // { fieldKey: [{id, label}] }

  const load = async () => {
    try { const r = await api.get(schema.path); setItems(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [schema.path]);

  // Fetch remote options once per schema
  useEffect(() => {
    (async () => {
      const next = {};
      for (const f of schema.fields || []) {
        if (f.type === "remote_select") {
          try {
            const r = await api.get(f.path);
            next[f.key] = r.data;
          } catch { next[f.key] = []; }
        }
      }
      setRemote(next);
    })();
    // eslint-disable-next-line
  }, [JSON.stringify(schema.fields)]);

  const openNew = () => {
    const init = {};
    schema.fields.forEach(f => {
      init[f.key] = f.type === "number" ? 0
        : f.type === "checkbox" ? !!f.default
        : (f.default ?? "");
    });
    setForm(init);
    setEditingId(null);
    setOpen(true);
  };

  const openEdit = (it) => {
    setForm({ ...it });
    setEditingId(it.id);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {};
    schema.fields.forEach(f => {
      if (f.uiOnly) return;  // helper-only field, not sent to backend
      let v = form[f.key];
      if (f.type === "number") v = Number(v);
      if (f.type === "checkbox") v = !!v;
      payload[f.key] = v === "" ? null : v;
    });
    // If any field depends on another to be enabled (e.g. profit_per_kg needs profit_override),
    // drop the dependent when its gating checkbox is false so backend auto-computes.
    schema.fields.forEach(f => {
      if (f.clearWhen && f.clearWhen(form)) delete payload[f.key];
    });
    try {
      if (editingId) { await api.put(`${schema.path}/${editingId}`, payload); toast.success("Updated"); }
      else { await api.post(schema.path, payload); toast.success("Created"); }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`${schema.path}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // ---- CSV import / template (opt-in via schema.csv) ----
  const csv = schema.csv;
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvResult, setCsvResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const r = await api.get(`${csv.path}/template`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = csv.templateFile || "template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadCsv = async () => {
    if (!csvFile) { toast.error("Pick a CSV file"); return; }
    setCsvBusy(true); setCsvResult(null);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      const r = await api.post(`${csv.path}/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCsvResult(r.data);
      const parts = [
        `${r.data.created || 0} created`,
        `${r.data.updated || 0} updated`,
        `${r.data.errors?.length || 0} errors`,
      ];
      toast.success(`Imported · ${parts.join(" · ")}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setCsvBusy(false);
  };

  return (
    <AppShell
      title={schema.title}
      subtitle={schema.subtitle}
      actions={
        <div className="flex items-center gap-2">
          {csv && (
            <>
              <button data-testid="crud-csv-template" onClick={downloadTemplate}
                      className="inline-flex items-center gap-2 h-10 px-4 border border-[#E5E2D9] hover:bg-[#F7F6F2] text-[#1A1A1A] rounded-md text-sm">
                <Download size={14} /> Template
              </button>
              <button data-testid="crud-csv-upload-open" onClick={() => { setCsvOpen(true); setCsvResult(null); setCsvFile(null); }}
                      className="inline-flex items-center gap-2 h-10 px-4 border border-[#E5E2D9] hover:bg-[#F7F6F2] text-[#1A1A1A] rounded-md text-sm">
                <UploadSimple size={14} /> Import CSV
              </button>
            </>
          )}
          <button data-testid="crud-add" onClick={openNew}
                  className="inline-flex items-center gap-2 h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium transition-colors">
            <Plus size={14} weight="bold" /> Add new
          </button>
        </div>
      }
    >
      <div className="bg-white border border-[#E5E2D9] rounded-md">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`crud-table-${schema.path.replace(/\//g, "-")}`}>
            <thead>
              <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
                {schema.columns.map(c => <th key={c.key} className="px-6 py-3">{c.label}</th>)}
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={schema.columns.length + 1} className="px-6 py-12 text-center text-[#8A8780]">
                  No records yet.
                </td></tr>
              )}
              {items.map((it, i) => (
                <tr key={it.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                  {schema.columns.map(c => (
                    <td key={c.key} className="px-6 py-3">{c.render ? c.render(it) : (it[c.key] ?? "—")}</td>
                  ))}
                  <td className="px-6 py-3 text-right">
                    <button data-testid={`crud-edit-${it.id}`} onClick={() => openEdit(it)}
                            className="text-[#2D5A27] text-xs inline-flex items-center gap-1 mr-4">
                      <PencilSimple size={12} />Edit
                    </button>
                    <button data-testid={`crud-del-${it.id}`} onClick={() => del(it.id)}
                            className="text-[#C25D38] text-xs inline-flex items-center gap-1">
                      <Trash size={12} />Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white border border-[#E5E2D9] rounded-md w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">{schema.subtitle}</div>
                <div className="font-heading text-lg">{editingId ? "Edit" : "New"} {schema.title}</div>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16} /></button>
            </div>
            <form onSubmit={submit} className="space-y-4" data-testid="crud-form">
              {schema.fields.map(f => {
                const hide = f.showIf && !f.showIf(form);
                if (hide) return null;
                return (
                  <div key={f.key}>
                    {f.type !== "checkbox" && (
                      <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">{f.label}</label>
                    )}
                    {f.type === "select" ? (
                      <select value={form[f.key] ?? ""} required={f.required}
                              data-testid={`crud-field-${f.key}`}
                              onChange={(e) => setForm(s => ({ ...s, [f.key]: e.target.value }))}
                              className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white">
                        {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : f.type === "remote_select" ? (
                      <select value={form[f.key] ?? ""} required={f.required}
                              data-testid={`crud-field-${f.key}`}
                              onChange={(e) => setForm(s => ({ ...s, [f.key]: e.target.value }))}
                              className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white">
                        <option value="">{f.placeholder || "— none —"}</option>
                        {(remote[f.key] || []).map(o =>
                          <option key={o.id} value={o.id}>{f.labelFn ? f.labelFn(o) : o.name}</option>
                        )}
                      </select>
                    ) : f.type === "checkbox" ? (
                      <label className="flex items-center gap-2 text-sm text-[#1A1A1A] cursor-pointer select-none">
                        <input type="checkbox" checked={!!form[f.key]}
                               data-testid={`crud-field-${f.key}`}
                               onChange={(e) => setForm(s => ({ ...s, [f.key]: e.target.checked }))}
                               className="accent-[#2D5A27] w-4 h-4" />
                        {f.label}
                      </label>
                    ) : (
                      <input type={f.type === "number" ? "number" : "text"}
                             step={f.type === "number" ? "any" : undefined}
                             value={form[f.key] ?? ""}
                             required={f.required}
                             data-testid={`crud-field-${f.key}`}
                             onChange={(e) => setForm(s => ({ ...s, [f.key]: e.target.value }))}
                             className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md" />
                    )}
                    {f.helper && <div className="text-[11px] text-[#8A8780] mt-1">{typeof f.helper === "function" ? f.helper(form) : f.helper}</div>}
                  </div>
                );
              })}
              <button type="submit" data-testid="crud-save"
                      className="w-full h-10 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium transition-colors">
                {editingId ? "Save changes" : "Create"}
              </button>
            </form>
          </div>
        </div>
      )}
      {csvOpen && csv && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setCsvOpen(false)}>
          <div className="bg-white border border-[#E5E2D9] rounded-md w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <FileCsv size={20} color="#2D5A27" weight="fill" />
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">CSV import</div>
                <div className="font-heading text-lg">{schema.title}</div>
              </div>
            </div>
            <div className="text-xs text-[#5C5A55] bg-[#F7F6F2] border border-[#E5E2D9] rounded p-3 mb-4">
              {csv.helpText || "Upload a CSV using the template layout. Existing rows are updated; new rows are created."}
            </div>
            <input type="file" accept=".csv,text/csv" data-testid="crud-csv-file"
                   onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                   className="w-full text-sm mb-4" />
            {csvResult && (
              <div className="text-xs bg-[#D4E0D1]/40 border border-[#D4E0D1] rounded p-3 mb-4" data-testid="crud-csv-result">
                Created: <b>{csvResult.created || 0}</b> · Updated: <b>{csvResult.updated || 0}</b> · Errors: <b>{csvResult.errors?.length || 0}</b>
                {csvResult.errors?.length > 0 && (
                  <ul className="mt-2 text-[11px] text-[#7A2E18] space-y-0.5">
                    {csvResult.errors.slice(0, 5).map((er, i) => (
                      <li key={i}>Row {er.row}: {er.error}</li>
                    ))}
                    {csvResult.errors.length > 5 && <li>… and {csvResult.errors.length - 5} more</li>}
                  </ul>
                )}
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => setCsvOpen(false)}
                      className="flex-1 h-10 border border-[#E5E2D9] rounded-md text-sm">Close</button>
              <button onClick={uploadCsv} disabled={csvBusy || !csvFile} data-testid="crud-csv-upload"
                      className="flex-1 h-10 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                {csvBusy ? "Uploading…" : "Upload"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
